import React, { createContext, useContext, useEffect, useRef } from "react";
import alertSound from "../../assets/media/alert_sound.mp3";
import repositionSound from "../../assets/media/reposition_sound.mp3";

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const leavingAudio = useRef(null);
  const repositionAudio = useRef(null);
  const isLeavingAudioPlaying = useRef(false);
  const isRepositionAudioPlaying = useRef(false);

  const resetLeavingAudio = () => {
    leavingAudio.current.currentTime = 0;
    isLeavingAudioPlaying.current = false;
  };

  const resetRepositionAudio = () => {
    repositionAudio.current.currentTime = 0;
    isRepositionAudioPlaying.current = false;
  };

  useEffect(() => {
    leavingAudio.current = new Audio(alertSound);
    leavingAudio.current.load();
    leavingAudio.current.addEventListener("pause", () => {
      resetLeavingAudio();
    });
    leavingAudio.current.addEventListener("ended", () => {
      resetLeavingAudio();
    });

    repositionAudio.current = new Audio(repositionSound);
    repositionAudio.current.load();
    repositionAudio.current.addEventListener("pause", () => {
      resetRepositionAudio();
    });
    repositionAudio.current.addEventListener("ended", () => {
      resetRepositionAudio();
    });
  }, []);

  // unlock audio on user interaction
  const unlockAudio = async () => {
    try {
      leavingAudio.current.muted = true;
      leavingAudio.current.play().then(() => {
        leavingAudio.current.pause();
        console.log("load leaving audio.");
      });

      repositionAudio.current.muted = true;
      repositionAudio.current.play().then(() => {
        repositionAudio.current.pause();
        console.log("load reposition audio.");
      });
    } catch (e) {
      console.log(`Audio Unlocked with errors: ${e.message}`);
    } finally {
      resetLeavingAudio();
      resetRepositionAudio();
    }
  };

  const playLeavingAudio = async () => {
    if (isLeavingAudioPlaying.current) return;
    try {
      isLeavingAudioPlaying.current = true;
      leavingAudio.current.muted = false;
      leavingAudio.current.play();
      await new Promise((resolve) => {
        leavingAudio.current.onended = resolve;
      });
      resetLeavingAudio();
    } catch (e) {
      console.log(`Leaving audio play failed: ${e.message}`);
    } finally {
      resetLeavingAudio();
    }
  };

  const playRepositionAudio = async () => {
    if (isRepositionAudioPlaying.current) return;
    try {
      isRepositionAudioPlaying.current = true;
      repositionAudio.current.muted = false;
      repositionAudio.current.currentTime = 0;
      repositionAudio.current.play();
      await new Promise((resolve) => {
        repositionAudio.current.onended = resolve;
      });
      resetRepositionAudio();
    } catch (e) {
      console.log(`Reposition audio play failed: ${e.message}`);
    } finally {
      resetRepositionAudio();
    }
  };

  return (
    <AudioContext.Provider
      value={{
        unlockAudio,
        playLeavingAudio,
        playRepositionAudio,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => useContext(AudioContext);
